/**
 * GitHub OAuth utilities.
 * Handles the OAuth flow, token storage, and GitHub API calls.
 */

import { getApiUrl } from './environment';

// localStorage keys
const GITHUB_TOKEN_KEY = 'talkyn-github-token';
const GITHUB_USER_KEY = 'talkyn-github-user';
const GITHUB_PROJECT_KEY = 'talkyn-github-project';
const GITHUB_OAUTH_STATE_KEY = 'talkyn-github-oauth-state';
const GITHUB_INSTALL_STATUS_KEY = 'talkyn-github-install-status';
const GITHUB_APP_SLUG_KEY = 'talkyn-github-app-slug';

// ── Token Management ──

export function getGitHubToken(): string | null {
  return localStorage.getItem(GITHUB_TOKEN_KEY);
}

export function setGitHubToken(token: string): void {
  localStorage.setItem(GITHUB_TOKEN_KEY, token);
}

export function clearGitHubToken(): void {
  localStorage.removeItem(GITHUB_TOKEN_KEY);
  localStorage.removeItem(GITHUB_USER_KEY);
  localStorage.removeItem(GITHUB_PROJECT_KEY);
  localStorage.removeItem(GITHUB_OAUTH_STATE_KEY);
  localStorage.removeItem(GITHUB_INSTALL_STATUS_KEY);
  localStorage.removeItem(GITHUB_APP_SLUG_KEY);
}

export function isGitHubConnected(): boolean {
  return !!getGitHubToken();
}

// ── App Installation ──

export function getAppSlug(): string {
  return localStorage.getItem(GITHUB_APP_SLUG_KEY) || 'talkyn-app';
}

export function setAppSlug(slug: string): void {
  localStorage.setItem(GITHUB_APP_SLUG_KEY, slug);
}

export function getInstallationStatus(): 'installed' | 'not-installed' | null {
  const raw = localStorage.getItem(GITHUB_INSTALL_STATUS_KEY);
  if (raw === 'installed' || raw === 'not-installed') return raw;
  return null;
}

export function setInstallationStatus(status: 'installed' | 'not-installed'): void {
  localStorage.setItem(GITHUB_INSTALL_STATUS_KEY, status);
}

/**
 * Check if the GitHub App is installed for the authenticated user.
 * Uses GET /user/installations which lists app installations accessible to the user token.
 * We check if any installation's app_slug matches our app.
 */
export async function checkAppInstallation(token?: string): Promise<boolean> {
  const t = token || getGitHubToken();
  if (!t) return false;

  try {
    const res = await fetch('https://api.github.com/user/installations', {
      headers: {
        Authorization: `Bearer ${t}`,
        Accept: 'application/vnd.github+json',
      },
    });

    if (!res.ok) return false;

    const data = await res.json();
    const slug = getAppSlug();
    const installed = (data.installations || []).some(
      (inst: { app_slug: string }) => inst.app_slug === slug
    );

    setInstallationStatus(installed ? 'installed' : 'not-installed');
    return installed;
  } catch {
    return false;
  }
}

/**
 * Get the URL to install the GitHub App.
 */
export function getInstallUrl(): string {
  const slug = getAppSlug();
  return `https://github.com/apps/${slug}/installations/new`;
}

// ── User Info ──

export interface GitHubUser {
  login: string;
  avatar_url: string;
  name: string | null;
}

export function getCachedGitHubUser(): GitHubUser | null {
  const raw = localStorage.getItem(GITHUB_USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setCachedGitHubUser(user: GitHubUser): void {
  localStorage.setItem(GITHUB_USER_KEY, JSON.stringify(user));
}

// ── Project Selection ──

export interface GitHubProject {
  id: string;  // node ID like PVT_xxxx
  title: string;
}

export function getSelectedProject(): GitHubProject | null {
  const raw = localStorage.getItem(GITHUB_PROJECT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === 'string' && typeof parsed.title === 'string') {
      return parsed as GitHubProject;
    }
    return null;
  } catch { return null; }
}

export function setSelectedProject(project: GitHubProject): void {
  localStorage.setItem(GITHUB_PROJECT_KEY, JSON.stringify(project));
}

export function clearSelectedProject(): void {
  localStorage.removeItem(GITHUB_PROJECT_KEY);
}

// ── OAuth Flow ──

/**
 * Start the GitHub OAuth flow.
 * Redirects the user to GitHub's authorize page.
 */
export async function startGitHubOAuth(): Promise<void> {
  const baseUrl = getApiUrl();
  const configRes = await fetch(`${baseUrl}/api/github/config`);
  if (!configRes.ok) {
    throw new Error('GitHub OAuth not configured on this server');
  }
  const { clientId, scopes, appSlug } = await configRes.json();

  // Cache app slug for installation URL
  if (appSlug) setAppSlug(appSlug);

  // Generate CSRF state
  const state = crypto.randomUUID();
  sessionStorage.setItem(GITHUB_OAUTH_STATE_KEY, state);

  // Build the callback URL based on current origin.
  // Ensure no double slashes: /app/ + github/callback → /app/github/callback
  const basePath = window.location.pathname.endsWith('/')
    ? window.location.pathname
    : window.location.pathname + '/';
  const redirectUri = `${window.location.origin}${basePath}github/callback`;

  const authorizeUrl = new URL('https://github.com/login/oauth/authorize');
  authorizeUrl.searchParams.set('client_id', clientId);
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('scope', scopes);
  authorizeUrl.searchParams.set('state', state);

  window.location.href = authorizeUrl.toString();
}

/**
 * Handle the OAuth callback. Extracts code from URL params,
 * verifies state, exchanges code for token.
 * Returns the access token if successful.
 */
export async function handleGitHubCallback(searchParams: URLSearchParams): Promise<string> {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');

  if (error) {
    throw new Error(`GitHub OAuth error: ${searchParams.get('error_description') || error}`);
  }

  if (!code) {
    throw new Error('No authorization code received from GitHub');
  }

  // Verify CSRF state
  const savedState = sessionStorage.getItem(GITHUB_OAUTH_STATE_KEY);
  if (!savedState || savedState !== state) {
    throw new Error('OAuth state mismatch — possible CSRF attack');
  }
  sessionStorage.removeItem(GITHUB_OAUTH_STATE_KEY);

  // Exchange code for token via our backend
  const baseUrl = getApiUrl();
  const exchangeRes = await fetch(`${baseUrl}/api/github/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!exchangeRes.ok) {
    const errData = await exchangeRes.json().catch(() => ({}));
    throw new Error((errData as { error?: string }).error || 'Token exchange failed');
  }

  const { access_token } = await exchangeRes.json();
  setGitHubToken(access_token);

  // Fetch and cache user info
  const user = await fetchGitHubUser(access_token);
  setCachedGitHubUser(user);

  // Check if app is installed (needed for ProjectsV2 access)
  await checkAppInstallation(access_token);

  return access_token;
}

// ── GitHub API Calls ──

/**
 * Fetch the authenticated user's info.
 */
export async function fetchGitHubUser(token?: string): Promise<GitHubUser> {
  const t = token || getGitHubToken();
  if (!t) throw new Error('Not connected to GitHub');

  const res = await fetch('https://api.github.com/user', {
    headers: { Authorization: `Bearer ${t}` },
  });
  if (!res.ok) {
    if (res.status === 401) {
      clearGitHubToken();
      throw new Error('GitHub token expired — please reconnect');
    }
    throw new Error('Failed to fetch GitHub user');
  }
  return res.json();
}

/**
 * Fetch the user's ProjectsV2 boards.
 */
export async function fetchGitHubProjects(token?: string): Promise<GitHubProject[]> {
  const t = token || getGitHubToken();
  if (!t) throw new Error('Not connected to GitHub');

  const query = `query {
    viewer {
      projectsV2(first: 20, orderBy: { field: UPDATED_AT, direction: DESC }) {
        nodes {
          id
          title
        }
      }
    }
  }`;

  const res = await fetch('https://api.github.com/graphql', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${t}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query }),
  });

  if (!res.ok) {
    if (res.status === 401) {
      clearGitHubToken();
      throw new Error('GitHub token expired — please reconnect');
    }
    throw new Error('Failed to fetch GitHub projects');
  }

  const data = await res.json();
  if (data.errors) {
    throw new Error(data.errors[0]?.message || 'GraphQL error');
  }

  const nodes: unknown[] = data.data?.viewer?.projectsV2?.nodes || [];
  return nodes.filter((n): n is GitHubProject =>
    n != null && typeof (n as GitHubProject).id === 'string' && typeof (n as GitHubProject).title === 'string'
  );
}

/**
 * Fetch a project board's data (columns + items).
 * This is the same query the backend was doing, but now from the frontend.
 */
export interface BoardItem {
  id: string;
  title: string;
  number: number;
  url: string;
  state: string;
  repo: string;
  status: string;
  labels: { name: string; color: string }[];
}

export interface BoardData {
  title: string;
  columns: string[];
  items: BoardItem[];
}

export async function fetchProjectBoard(projectId: string, token?: string): Promise<BoardData> {
  const t = token || getGitHubToken();
  if (!t) throw new Error('Not connected to GitHub');

  const query = `query($projectId: ID!) {
    node(id: $projectId) {
      ... on ProjectV2 {
        title
        items(first: 100) {
          nodes {
            id
            content {
              ... on Issue {
                title
                number
                url
                state
                repository { nameWithOwner }
                labels(first: 10) {
                  nodes { name color }
                }
              }
              ... on PullRequest {
                title
                number
                url
                state
                repository { nameWithOwner }
                labels(first: 10) {
                  nodes { name color }
                }
              }
            }
            fieldValues(first: 10) {
              nodes {
                ... on ProjectV2ItemFieldSingleSelectValue {
                  name
                  field { ... on ProjectV2FieldCommon { name } }
                }
              }
            }
          }
        }
      }
    }
  }`;

  const res = await fetch('https://api.github.com/graphql', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${t}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, variables: { projectId } }),
  });

  if (!res.ok) {
    if (res.status === 401) {
      clearGitHubToken();
      throw new Error('GitHub token expired — please reconnect');
    }
    throw new Error('Failed to fetch project board');
  }

  const data = await res.json();
  if (data.errors) {
    throw new Error(data.errors[0]?.message || 'GraphQL error');
  }

  const project = data.data?.node;
  if (!project) {
    throw new Error('Project not found — check your project selection');
  }

  const columns = ['Backlog', 'Spec', 'In Progress', 'Review', 'Merge', 'Verify', 'Done'];

  const items: BoardItem[] = (project.items?.nodes || [])
    .filter((node: Record<string, unknown>) => node.content)
    .map((node: Record<string, unknown>) => {
      const content = node.content as Record<string, unknown>;
      let status = 'Backlog';
      const fieldValues = node.fieldValues as { nodes?: Array<Record<string, unknown>> } | undefined;
      for (const fv of (fieldValues?.nodes || [])) {
        const field = fv.field as { name?: string } | undefined;
        if (field?.name === 'Status' && fv.name) {
          status = fv.name as string;
          break;
        }
      }
      const repository = content.repository as { nameWithOwner?: string } | undefined;
      const labels = content.labels as { nodes?: Array<{ name: string; color: string }> } | undefined;
      return {
        id: node.id as string,
        title: (content.title as string) || '',
        number: (content.number as number) || 0,
        url: (content.url as string) || '',
        state: (content.state as string) || '',
        repo: repository?.nameWithOwner || '',
        status,
        labels: (labels?.nodes || []).map((l) => ({
          name: l.name,
          color: l.color,
        })),
      };
    });

  return { title: (project.title as string) || 'Project Board', columns, items };
}
